"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { enUS, es, Locale } from "date-fns/locale"

// Import language files
import en from "@/locales/en.json"
import esTranslations from "@/locales/es.json"

// Define available languages
type Translations = Record<string, any>;

export const languages = {
  en: { code: "en", flag: "🇬🇧", translations: en as Translations, dateLocale: enUS },
  es: { code: "es", flag: "🇪🇸", translations: esTranslations as Translations, dateLocale: es },
}

export type LanguageCode = keyof typeof languages

type LanguageContextType = {
  language: LanguageCode
  setLanguage: (code: LanguageCode, saveToSettings?: boolean) => void
  t: (key: string, variables?: Record<string, string | number>) => string
  dateLocale: Locale
  loadLanguageFromSettings: (settingsLanguage: string) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Get initial language from localStorage or default to Spanish (system default)
  const [language, setLanguageState] = useState<LanguageCode>("es")
  const [isInitialized, setIsInitialized] = useState(false)

  // Load saved language preference on initial render
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem("casinopay-language") as LanguageCode
      if (savedLanguage && languages[savedLanguage]) {
        setLanguageState(savedLanguage)
      }
      setIsInitialized(true)
    } catch (error) {
      console.error("Error loading language preference:", error)
      // Default to Spanish if there's an error
      setLanguageState("es")
      setIsInitialized(true)
    }
  }, [])

  // Function to load language from user settings (higher priority than localStorage)
  const loadLanguageFromSettings = (settingsLanguage: string) => {
    const langCode = settingsLanguage as LanguageCode
    if (languages[langCode]) {
      setLanguageState(langCode)
      // Also update localStorage to keep them in sync
      try {
        localStorage.setItem("casinopay-language", langCode)
      } catch (error) {
        console.error("Error saving language to localStorage:", error)
      }
    }
  }

  // Update language and save to localStorage (and optionally to user settings)
  const setLanguage = (code: LanguageCode, saveToSettings: boolean = false) => {
    try {
      setLanguageState(code)
      localStorage.setItem("casinopay-language", code)
      
      // Expose current language globally for service translations
      ;(window as any).__LANGUAGE_CONTEXT__ = {
        current: languages[code]
      }

      // If saveToSettings is true, trigger settings update
      if (saveToSettings && isInitialized) {
        // Dispatch a custom event that settings components can listen to
        window.dispatchEvent(new CustomEvent('languageChangedFromContext', {
          detail: { language: code }
        }))
      }
    } catch (error) {
      console.error("Error saving language preference:", error)
    }
  }

  // Translation function with variable interpolation
  const t = (key: string, variables?: Record<string, string | number>): string => {
    try {
      const keys = key.split(".")
      let value: any = languages[language]?.translations || languages.en.translations

      for (const k of keys) {
        if (value && typeof value === "object" && k in value) {
          value = value[k]
        } else {
          // Fallback to English if translation is missing
          let fallback = languages.en.translations
          for (const fk of keys) {
            if (fallback && typeof fallback === "object" && fk in fallback) {
              fallback = fallback[fk]
            } else {
              return key // Return the key if translation is missing in both languages
            }
          }
          value = fallback
          break
        }
      }

      let result = typeof value === "string" ? value : key

      // Replace variables in the format {variableName}
      if (variables && typeof result === "string") {
        Object.entries(variables).forEach(([varKey, varValue]) => {
          const placeholder = `{${varKey}}`
          result = result.replace(new RegExp(placeholder, 'g'), String(varValue))
        })
      }

      return result
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error)
      return key
    }
  }

  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        setLanguage, 
        t, 
        dateLocale: languages[language].dateLocale,
        loadLanguageFromSettings
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

// Custom hook to use the language context
export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}